import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, UserCheck, UserX, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PublicLayout } from "@/components/PublicLayout";
import { childrenAPI } from "@/lib/api";
import { toast } from "sonner";
import { parse, isValid } from "date-fns";

export default function VerifyChildPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [birthDateInput, setBirthDateInput] = useState("");
  const [childName, setChildName] = useState("");
  const [verificationResult, setVerificationResult] = useState(null);

  // Format date input as DD/MM/AAAA
  const handleDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
    
    if (value.length > 8) value = value.slice(0, 8);
    
    // Add slashes automatically
    if (value.length >= 5) {
      value = value.slice(0, 2) + "/" + value.slice(2, 4) + "/" + value.slice(4);
    } else if (value.length >= 3) {
      value = value.slice(0, 2) + "/" + value.slice(2);
    }
    
    setBirthDateInput(value);
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for API
  const convertToApiFormat = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return null;
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  };

  // Validate date
  const isValidDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return false;
    const apiFormat = convertToApiFormat(dateStr);
    if (!apiFormat) return false;
    const parsed = parse(apiFormat, "yyyy-MM-dd", new Date());
    return isValid(parsed) && parsed <= new Date();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!childName.trim()) {
      toast.error("Informe o nome da criança");
      return;
    }

    if (!isValidDate(birthDateInput)) {
      toast.error("Data de nascimento inválida. Use o formato DD/MM/AAAA");
      return;
    }

    setIsLoading(true);
    setVerificationResult(null);

    try {
      const response = await childrenAPI.verify({
        full_name: childName.trim(),
        date_of_birth: convertToApiFormat(birthDateInput),
      });

      setVerificationResult(response.data);

      if (response.data.found) {
        if (response.data.has_booking) {
          toast.info("Esta criança já possui um agendamento!");
        } else {
          toast.success("Criança encontrada! Selecione um workshop.");
        }
      } else {
        toast.info("Criança não encontrada. Faça o cadastro.");
      }
    } catch (error) {
      toast.error("Erro ao verificar cadastro");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceed = () => {
    if (verificationResult?.found && !verificationResult?.has_booking) {
      navigate(`/agendar/${verificationResult.child.id}`);
    } else if (!verificationResult?.found) {
      // Pass the entered data to the registration page
      navigate("/cadastrar", { 
        state: { 
          childName: childName.trim(),
          dateOfBirth: convertToApiFormat(birthDateInput) || ""
        }
      });
    }
  };

  return (
    <PublicLayout showBack backTo="/">
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <Card className="glass-card shadow-2xl animate-scale-in">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="font-nunito text-2xl">Verificar Cadastro</CardTitle>
              <CardDescription>
                Informe os dados da criança para verificar se ela está cadastrada
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerify} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="childName">Nome Completo da Criança</Label>
                  <Input
                    id="childName"
                    value={childName}
                    onChange={(e) => setChildName(e.target.value)}
                    placeholder="Digite o nome completo"
                    className="h-12 rounded-xl text-base"
                    required
                    data-testid="verify-child-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="birthDate">Data de Nascimento</Label>
                  <Input
                    id="birthDate"
                    value={birthDateInput}
                    onChange={handleDateChange}
                    placeholder="DD/MM/AAAA"
                    inputMode="numeric"
                    maxLength={10}
                    className="h-12 rounded-xl text-base"
                    data-testid="verify-birthdate-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Digite no formato: dia/mês/ano (ex: 10/05/2015)
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full btn-animate text-base"
                  disabled={isLoading}
                  data-testid="verify-submit-button"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      Verificar Cadastro
                    </>
                  )}
                </Button>
              </form>

              {/* Verification Result */}
              {verificationResult && (
                <div className="mt-6 animate-fade-in">
                  {verificationResult.found ? (
                    <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                      <div className="flex items-center gap-3 mb-3">
                        <UserCheck className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                        <span className="font-medium text-emerald-700 dark:text-emerald-300">
                          Criança Cadastrada!
                        </span>
                      </div>
                      <div className="space-y-1 text-sm mb-4">
                        <p><strong>Nome:</strong> {verificationResult.child.full_name}</p>
                        <p><strong>Idade:</strong> {verificationResult.child.age} anos</p>
                        <p><strong>Escola:</strong> {verificationResult.child.school_name}</p>
                      </div>
                      
                      {verificationResult.has_booking ? (
                        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                            <Calendar className="w-5 h-5" />
                            <span className="font-medium">Já possui agendamento</span>
                          </div>
                          <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                            {verificationResult.booking.workshop_type === "robotica" ? "Robótica" : "Atleta Digital"} - {" "}
                            {verificationResult.booking.workshop_date} às {verificationResult.booking.workshop_time}
                          </p>
                        </div>
                      ) : (
                        <Button
                          onClick={handleProceed}
                          className="w-full rounded-full btn-animate"
                          data-testid="proceed-to-schedule-button"
                        >
                          Escolher Workshop
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <div className="flex items-center gap-3 mb-3">
                        <UserX className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                        <span className="font-medium text-amber-700 dark:text-amber-300">
                          Criança Não Encontrada
                        </span>
                      </div>
                      <p className="text-sm text-amber-600 dark:text-amber-400 mb-4">
                        Não encontramos cadastro com esses dados. Faça o cadastro para poder agendar.
                      </p>
                      <Button
                        onClick={handleProceed}
                        className="w-full rounded-full btn-animate"
                        data-testid="proceed-to-register-button"
                      >
                        Cadastrar Criança
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
}
